import { createFileRoute } from '@tanstack/react-router';
import type { Row } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import type { ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import { DataTable, DataTablePagination } from '@/components/DataTable';
import { FillScreen, HStack, ListToolbar } from '@/components/Layout';
import SearchInput from '@/components/SearchInput';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { useLicenseSoftwareAppContract } from '../-hooks/useLicenseSoftwareAppContract';
import CategoryTabs from './-components/CategoryTabs';
import { DeviceAssignmentDialog } from './-components/device-assignment-dialog';
import { InventoryTabs } from './-components/InventoryTabs';
import { UserAssignmentDialog } from './-components/user-assignment-dialog';
import { EMPTY_CATEGORY_COUNTS, SearchSchema, TabKey, USER_BASED_CATEGORY } from './-constants';
import { useLicenseDeviceTable } from './-hooks/useLicenseDeviceTable';
import { useTab } from './-hooks/useTab';
import { useLicenseAssignmentsCount } from './-query';
import type { LicenseDeviceTableRow } from './-types';
export const Route = createFileRoute('/_staff/licenses/$id/_profile/assignments/')({
  validateSearch: zodValidator(SearchSchema),
  component: LicenseDevices
});
function LicenseDevices() {
  const {
    id
  } = Route.useParams();
  const search = Route.useSearch();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.licenses.detailDevices'
  });
  const {
    t: tAssignments
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.licenses.assignments'
  });
  const navigate = Route.useNavigate();
  const {
    table,
    displayState,
    allocationType,
    totalCount,
    software,
    licenseName,
    canRetrieveLicenseService,
    canRetrieveSubGroup
  } = useLicenseDeviceTable();

  // USER_BASED用のタブ
  const {
    category,
    handleCategoryChange
  } = useTab();
  const {
    data: categoryCounts
  } = useLicenseAssignmentsCount(id);

  // 排他モデル×サブグループ無し（GWS 型）では per-license ルールが評価に使われず、
  // ルール由来のカテゴリ分類（FOLLOWING/VIOLATED 等）が成立しないため ALL のみ表示する
  const {
    isCrossLicenseRuleOnly
  } = useLicenseSoftwareAppContract({
    softwareId: software?.id,
    canRetrieveLicenseService
  });
  useEffect(() => {
    if (isCrossLicenseRuleOnly && category !== USER_BASED_CATEGORY.ALL) {
      // 一覧クエリは URL の category を参照するため、URL ごと ALL に強制する。
      // 履歴に強制前のカテゴリを残さないよう replace で遷移する
      navigate({
        search: prev => ({
          ...prev,
          category: USER_BASED_CATEGORY.ALL,
          pageIndex: 0
        }),
        replace: true
      });
    }
  }, [isCrossLicenseRuleOnly, category, navigate]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState(() => search.freeText ?? '');
  const isFocusedRef = useRef(false);

  // ブラウザの戻る/進む等でURL側が変わった場合の同期
  useEffect(() => {
    if (!isFocusedRef.current) {
      setSearchText(search.freeText ?? '');
    }
  }, [search.freeText]);
  const debouncedSearch = useDebouncedCallback((text: string) => navigate({
    search: prev => ({
      ...prev,
      freeText: text || undefined,
      pageIndex: 0
    })
  }), [navigate]);
  const handleFilterInput = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchText(newValue);
    debouncedSearch(newValue);
  };
  const handleFocus = () => {
    isFocusedRef.current = true;
  };
  const handleBlur = () => {
    isFocusedRef.current = false;
  };
  const handleRowClick = (row: Row<LicenseDeviceTableRow>) => {
    const data = row.original;
    if (data.type === 'USER_BASED') {
      if (data.worker?.id) {
        navigate({
          to: '/person/$id',
          params: {
            id: data.worker.id
          }
        });
      }
    } else {
      if (data.device?.id) {
        navigate({
          to: '/devices/$id',
          params: {
            id: data.device.id
          }
        });
      }
    }
  };
  return <FillScreen className="flex flex-col gap-4 px-12 py-5">
      {allocationType === 'USER_BASED' ? <>
          <CategoryTabs category={category} counts={categoryCounts ?? EMPTY_CATEGORY_COUNTS} canRetrieveSubGroup={canRetrieveSubGroup} showsAllCategoryOnly={isCrossLicenseRuleOnly} onCategoryChange={handleCategoryChange}>
            {software && !canRetrieveLicenseService && <ActionButton variant="secondary" size="sm" onClick={() => setIsOpen(true)} className="shrink-0">
                {tAssignments('assignButton')}
              </ActionButton>}
          </CategoryTabs>
          <ListToolbar className="w-full rounded bg-background p-2">
            <SearchInput className="h-8 max-w-[320px] w-full" placeholder={tAssignments('searchPlaceholder')} value={searchText} onChange={handleFilterInput} onFocus={handleFocus} onBlur={handleBlur} />
          </ListToolbar>
          {software && !canRetrieveLicenseService && <UserAssignmentDialog licenseId={id} licenseName={licenseName} software={{
        id: software.id,
        name: software.name,
        logoUrl: software.logoUrl,
        externalTenantName: software.externalTenantName
      }} isOpen={isOpen} onClose={() => setIsOpen(false)} onSuccess={() => {}} />}
        </> : <HStack className="gap-2 items-center">
          <InventoryTabs counts={{
        [TabKey.ASSIGNED]: totalCount
      }} onChange={() => {}} value={TabKey.ASSIGNED} />
          <span className="ml-auto">
            <ActionButton variant="secondary" size="sm" onClick={() => setIsOpen(true)}>
              {t('assignButton')}
            </ActionButton>
          </span>
          <DeviceAssignmentDialog licenseId={id} title={t('deviceAssignmentDialog.title')} isOpen={isOpen} onClose={() => setIsOpen(false)} onSuccess={() => {}} />
        </HStack>}
      <div className="overflow-auto flex-1 min-h-0">
        <DataTable table={table} displayState={displayState} onRowClick={handleRowClick} isHoverable />
      </div>
      <DataTablePagination table={table} totalCount={totalCount} />
    </FillScreen>;
}